import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { DetailHeader } from "@/components/layout/detail-header";
import { api } from "@/lib/api";
import { useToast } from "@/contexts/toast-context";
import { ChevronRight, ChevronDown, Database, FileText, Folder, Search, RefreshCw } from "lucide-react";

interface Test {
  id: string;
  name: string;
  description?: string;
  group?: string;
  isAutomatable: boolean;
  createdAt: string;
  credentials?: {
    apiKey?: string;
  };
}

interface Space {
  id: string;
  name: string;
  expanded?: boolean;
  workbooksExpanded?: boolean;
  filesExpanded?: boolean;
  isWorkbooksLoading?: boolean;
  isFilesLoading?: boolean;
  workbooks?: Workbook[];
}

interface Workbook {
  id: string;
  name: string;
  expanded?: boolean;
  sheets?: Sheet[];
}

interface Sheet {
  id: string;
  name: string;
}

interface File {
  id: string;
  name: string;
}

interface ExplorerData {
  spaces: Space[];
  files: { [spaceId: string]: File[] };
}

interface Pagination {
  currentPage: number;
  pageCount: number;
  totalCount: number;
}

export function TestExplorerPage() {
  const { testId } = useParams<{ testId: string }>();
  const navigate = useNavigate();
  const { showError } = useToast();
  
  const [test, setTest] = useState<Test | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingExplorer, setIsLoadingExplorer] = useState(false);
  const [selectedNode, setSelectedNode] = useState<any>(null);
  const [explorerData, setExplorerData] = useState<ExplorerData>({
    spaces: [],
    files: {}
  });
  const [explorerPagination, setExplorerPagination] = useState({
    spaces: { currentPage: 1, pageCount: 1, totalCount: 0 },
    files: { currentPage: 1, pageCount: 1, totalCount: 0 }
  });

  // Load test data
  useEffect(() => {
    if (testId) {
      loadTest();
      loadExplorerData();
    }
  }, [testId]);

  const loadTest = async () => {
    if (!testId) return;
    
    try {
      setIsLoading(true);
      const testData = await api.get<Test>(`/test/${testId}`);
      setTest(testData);
    } catch (error) {
      console.error("Failed to load test:", error);
      showError("Failed to load test", "Could not load test details. Please try again.");
      navigate("/app/tests");
    } finally {
      setIsLoading(false);
    }
  };

  const loadExplorerData = async () => {
    if (!testId) return;
    
    setSelectedNode(null);
    setIsLoadingExplorer(true);
    
    try {
      // Initialize explorerData with empty arrays
      setExplorerData({
        spaces: [],
        files: {}
      });
      
      // Load spaces with pagination
      const spacesResponse = await api.get<{data: Space[], pagination: Pagination}>(
        `/test/${testId}/explorer/spaces?page=${explorerPagination.spaces.currentPage}`
      );
      
      // Handle different response formats
      const responseData = (spacesResponse as any).data || spacesResponse;
      const responsePagination = (spacesResponse as any).pagination;
      
      // Update pagination data for spaces
      if (responsePagination) {
        setExplorerPagination(prev => ({
          ...prev,
          spaces: {
            currentPage: responsePagination.currentPage || 1,
            pageCount: responsePagination.pageCount || 1,
            totalCount: responsePagination.totalCount || 0
          }
        }));
      }
      
      // Add expanded properties to spaces
      const spaces = (Array.isArray(responseData) ? responseData : []).map((space: any) => ({ 
        ...space, 
        expanded: false,
        id: space.id || space.spaceId, // Ensure we have an id
        name: space.name || 'Unnamed Space'
      }));
      
      setExplorerData({
        spaces,
        files: {}
      });
    } catch (error) {
      console.error('Error loading explorer data:', error);
      showError("Failed to load explorer data", "Could not load explorer data. Please try again.");
    } finally {
      setIsLoadingExplorer(false);
    }
  };

  const loadWorkbooksForSpace = async (space: Space) => {
    if (!space || !testId) return;

    setExplorerData(prev => ({
      ...prev,
      spaces: prev.spaces.map(s => 
        s.id === space.id 
          ? { ...s, isWorkbooksLoading: true }
          : s
      )
    }));
    
    try {
      const response = await api.get<{data: Workbook[]}>(`/test/${testId}/explorer/workbooks?spaceId=${space.id}`);
      
      // Handle different response formats
      const responseData = (response as any).data || response;
      
      const workbooks = (Array.isArray(responseData) ? responseData : []).map((workbook: any) => ({
        ...workbook,
        expanded: false,
        id: workbook.id || workbook.workbookId,
        name: workbook.name || 'Unnamed Workbook',
      }));

      setExplorerData(prev => ({
        ...prev,
        spaces: prev.spaces.map(s => 
          s.id === space.id 
            ? { ...s, workbooks, isWorkbooksLoading: false }
            : s
        )
      }));
    } catch (error) {
      console.error('Error loading workbooks:', error);
      showError("Failed to load workbooks", "Could not load workbooks for this space.");
      setExplorerData(prev => ({
        ...prev,
        spaces: prev.spaces.map(s => 
          s.id === space.id 
            ? { ...s, isWorkbooksLoading: false }
            : s
        )
      }));
    }
  };

  const loadFilesForSpace = async (space: Space) => {
    if (!space || !testId) return;
    
    setExplorerData(prev => ({
      ...prev,
      spaces: prev.spaces.map(s => 
        s.id === space.id 
          ? { ...s, isFilesLoading: true }
          : s
      )
    }));

    try {
      const response = await api.get<{data: File[], pagination: Pagination}>(
        `/test/${testId}/explorer/files?spaceId=${space.id}&page=${explorerPagination.files.currentPage}`
      );
      
      // Handle different response formats
      const responseData = (response as any).data || response;
      const responsePagination = (response as any).pagination;
      
      // Update pagination data for files
      if (responsePagination) {
        setExplorerPagination(prev => ({
          ...prev,
          files: {
            currentPage: responsePagination.currentPage || 1,
            pageCount: responsePagination.pageCount || 1,
            totalCount: responsePagination.totalCount || 0
          }
        }));
      }

      // Store files for this space
      const files = (Array.isArray(responseData) ? responseData : []).map((file: any) => ({
        ...file,
        id: file.id || file.fileId,
        name: file.name || 'Unnamed File'
      }));

      setExplorerData(prev => ({
        ...prev,
        files: { ...prev.files, [space.id]: files },
        spaces: prev.spaces.map(s => 
          s.id === space.id 
            ? { ...s, isFilesLoading: false }
            : s
        )
      }));
    } catch (error) {
      console.error('Error loading files:', error);
      showError("Failed to load files", "Could not load files for this space.");
      setExplorerData(prev => ({
        ...prev,
        spaces: prev.spaces.map(s => 
          s.id === space.id 
            ? { ...s, isFilesLoading: false }
            : s
        )
      }));
    }
  };

  // Toggle functions
  const toggleSpace = async (space: Space) => {
    if (!space) return;

    // Close any other expanded space first
    setExplorerData(prev => ({
      ...prev,
      spaces: prev.spaces.map(s => 
        s.id !== space.id 
          ? { 
              ...s, 
              expanded: false, 
              workbooksExpanded: false, 
              filesExpanded: false,
              isWorkbooksLoading: false,
              isFilesLoading: false
            }
          : { 
              ...s, 
              expanded: !s.expanded,
              isWorkbooksLoading: !s.expanded,
              isFilesLoading: !s.expanded
            }
      )
    }));

    // Reset files pagination when switching spaces
    setExplorerPagination(prev => ({
      ...prev,
      files: { currentPage: 1, pageCount: 0, totalCount: 0 }
    }));

    // Load data for the space if expanding
    const updatedSpace = explorerData.spaces.find(s => s.id === space.id);
    if (updatedSpace && !updatedSpace.expanded) {
      await loadWorkbooksForSpace(space);
      await loadFilesForSpace(space);
    }
  };

  const toggleWorkbooksSection = async (space: Space) => {
    if (!space.workbooks) {
      await loadWorkbooksForSpace(space);
    }
    
    setExplorerData(prev => ({
      ...prev,
      spaces: prev.spaces.map(s => 
        s.id === space.id 
          ? { ...s, workbooksExpanded: !s.workbooksExpanded }
          : s
      )
    }));
  };

  const toggleFilesSection = (space: Space) => {
    setExplorerData(prev => ({
      ...prev,
      spaces: prev.spaces.map(s => 
        s.id === space.id 
          ? { ...s, filesExpanded: !s.filesExpanded }
          : s
      )
    }));
  };

  const toggleWorkbook = async (workbook: Workbook) => {
    // If expanding and sheets not loaded, load them
    if (!workbook.expanded && !workbook.sheets) {
      try {
        // Note: This API might not exist yet - keeping it for future implementation
        // const response = await api.get(`/test/${testId}/explorer/workbooks/${workbook.id}/sheets`);
        // const sheets = response.data.map(sheet => ({
        //   ...sheet,
        //   id: sheet.id || sheet.sheetId,
        //   name: sheet.name || 'Unnamed Sheet'
        // }));
        
        // For now, just toggle the expanded state
        setExplorerData(prev => ({
          ...prev,
          spaces: prev.spaces.map(space => ({
            ...space,
            workbooks: space.workbooks?.map(wb => 
              wb.id === workbook.id 
                ? { ...wb, expanded: !wb.expanded }
                : wb
            )
          }))
        }));
      } catch (error) {
        console.error('Error loading sheets for workbook:', error);
        showError("Failed to load sheets", "Could not load sheets for this workbook.");
      }
    } else {
      // Just toggle
      setExplorerData(prev => ({
        ...prev,
        spaces: prev.spaces.map(space => ({
          ...space,
          workbooks: space.workbooks?.map(wb => 
            wb.id === workbook.id 
              ? { ...wb, expanded: !wb.expanded }
              : wb
          )
        }))
      }));
    }
  };

  // Node selection
  const selectNode = (node: any, type: 'space' | 'workbook' | 'file') => {
    let data = node;
    if (type === 'workbook') {
      data = {
        ...node,
        sheets: node.sheets?.map((sheet: Sheet) => sheet.name) || []
      };
    }
    setSelectedNode(data);
  };

  // Pagination functions
  const changeSpacesPage = async (page: number) => {
    setExplorerPagination(prev => ({
      ...prev,
      spaces: { ...prev.spaces, currentPage: page }
    }));
    await loadExplorerData();
  };

  const changeFilesPage = async (spaceId: string, page: number) => {
    setExplorerPagination(prev => ({
      ...prev,
      files: { ...prev.files, currentPage: page }
    }));
    const space = explorerData.spaces.find(s => s.id === spaceId);
    if (space) {
      await loadFilesForSpace(space);
      setExplorerData(prev => ({
        ...prev,
        spaces: prev.spaces.map(s => 
          s.id === spaceId 
            ? { ...s, filesExpanded: true }
            : s
        )
      }));
    }
  };

  // Get files for a specific space
  const getFilesForSpace = (spaceId: string): File[] => {
    return explorerData.files[spaceId] || [];
  };

  // Utility function to truncate long file names
  const truncateFileName = (name: string, maxLength = 50): string => {
    if (name.length <= maxLength) return name;
    return `${name.substring(0, maxLength)}...`;
  };

  const handleToggleChange = (tabId: string) => {
    if (tabId === "details") {
      navigate(`/app/tests/${testId}`);
    } else {
      navigate(`/app/tests/${testId}/${tabId}`);
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-center flex-1">
          <div className="text-sm text-muted-foreground">Loading test details...</div>
        </div>
      </div>
    );
  }

  if (!test) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-center flex-1">
          <div className="text-sm text-muted-foreground">Test not found</div>
        </div>
      </div>
    );
  }

  const breadcrumb = [
    { label: "All Tests", href: "/app/tests" },
    { label: test.name }
  ];

  const toggleOptions = [
    { id: "details", label: "Details" },
    { id: "explorer", label: "Explorer" },
    { id: "files", label: "Files" },
    { id: "flow", label: "Flow" },
    { id: "expectations", label: "Expectations" },
    { id: "runs", label: "Runs" },
  ];

  const headerActions = (
    <Button
      onClick={loadExplorerData}
      disabled={isLoadingExplorer}
      size="sm"
      className="text-xs"
    >
      <RefreshCw className={`w-4 h-4 mr-1 ${isLoadingExplorer ? 'animate-spin' : ''}`} />
      Refresh
    </Button>
  );

  return (
    <div className="flex flex-col h-full">
      <DetailHeader
        breadcrumb={breadcrumb}
        toggleOptions={toggleOptions}
        activeToggle="explorer"
        onToggleChange={handleToggleChange}
        actions={headerActions}
      />

      <div className="flex-1 overflow-hidden">
        <div className="flex h-full">
          {/* Left Pane - Tree View */}
          <div className="flex flex-col border-r border-border" style={{ width: '35%' }}>
            <div className="p-4 border-b border-border">
              <h3 className="text-base font-semibold">Explorer</h3>
            </div>
            
            <div className="flex-1 overflow-auto p-4">
              {/* Spaces Pagination */}
              {explorerPagination.spaces.pageCount > 1 && (
                <div className="flex items-center justify-between mb-4 p-2 bg-muted rounded-md">
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={explorerPagination.spaces.currentPage === 1}
                    onClick={() => changeSpacesPage(explorerPagination.spaces.currentPage - 1)}
                    className="text-xs"
                  >
                    Previous
                  </Button>
                  <span className="text-xs text-muted-foreground">
                    {explorerPagination.spaces.currentPage} / {explorerPagination.spaces.pageCount}
                  </span>
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={explorerPagination.spaces.currentPage === explorerPagination.spaces.pageCount}
                    onClick={() => changeSpacesPage(explorerPagination.spaces.currentPage + 1)}
                    className="text-xs"
                  >
                    Next
                  </Button>
                </div>
              )}

              {/* Loading State */}
              {isLoadingExplorer ? (
                <div className="flex items-center justify-center h-32">
                  <RefreshCw className="w-6 h-6 animate-spin text-muted-foreground" />
                </div>
              ) : explorerData.spaces.length > 0 ? (
                <div className="space-y-2">
                  {/* Spaces */}
                  {explorerData.spaces.map((space) => (
                    <div key={space.id} className="border border-border rounded-md">
                      {/* Space Header */}
                      <div className="flex items-center justify-between p-3 hover:bg-muted/50 cursor-pointer">
                        <div className="flex items-center gap-2 flex-1" onClick={() => toggleSpace(space)}>
                          <Database className="w-4 h-4 text-primary" />
                          <span className="text-sm font-medium">{space.name}</span>
                          {space.expanded ? (
                            <ChevronDown className="w-4 h-4 text-muted-foreground" />
                          ) : (
                            <ChevronRight className="w-4 h-4 text-muted-foreground" />
                          )}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => selectNode(space, 'space')}
                          className="text-xs p-1 h-auto"
                        >
                          <Search className="w-3 h-3" />
                        </Button>
                      </div>

                      {/* Space Content - Workbooks and Files */}
                      {space.expanded && (
                        <div className="border-t border-border bg-muted/20">
                          {/* Workbooks Section */}
                          <div className="border-b border-border">
                            <div 
                              className="flex items-center justify-between p-3 hover:bg-muted/50 cursor-pointer"
                              onClick={() => toggleWorkbooksSection(space)}
                            >
                              <div className="flex items-center gap-2">
                                {space.isWorkbooksLoading ? (
                                  <RefreshCw className="w-4 h-4 animate-spin text-muted-foreground" />
                                ) : (
                                  <Folder className="w-4 h-4 text-orange-500" />
                                )}
                                <span className="text-sm">
                                  Workbooks
                                  {space.workbooks && space.workbooks.length > 0 && (
                                    <span className="ml-1 text-xs text-muted-foreground">
                                      ({space.workbooks.length})
                                    </span>
                                  )}
                                </span>
                              </div>
                              {space.workbooksExpanded ? (
                                <ChevronDown className="w-4 h-4 text-muted-foreground" />
                              ) : (
                                <ChevronRight className="w-4 h-4 text-muted-foreground" />
                              )}
                            </div>
                            
                            {space.workbooksExpanded && space.workbooks && (
                              <div className="bg-background/50">
                                {space.workbooks.map((workbook) => (
                                  <div key={workbook.id} className="border-b border-border last:border-b-0">
                                    <div className="flex items-center justify-between p-3 pl-8 hover:bg-muted/50 cursor-pointer">
                                      <div className="flex items-center gap-2 flex-1" onClick={() => toggleWorkbook(workbook)}>
                                        <Folder className="w-4 h-4 text-blue-500" />
                                        <span className="text-sm">
                                          {workbook.name}
                                          {workbook.sheets && workbook.sheets.length > 0 && (
                                            <span className="ml-1 text-xs text-muted-foreground">
                                              ({workbook.sheets.length})
                                            </span>
                                          )}
                                        </span>
                                        {workbook.expanded ? (
                                          <ChevronDown className="w-3 h-3 text-muted-foreground" />
                                        ) : (
                                          <ChevronRight className="w-3 h-3 text-muted-foreground" />
                                        )}
                                      </div>
                                      <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => selectNode(workbook, 'workbook')}
                                        className="text-xs p-1 h-auto"
                                      >
                                        <Search className="w-3 h-3" />
                                      </Button>
                                    </div>
                                    
                                    {/* Sheets */}
                                    {workbook.expanded && workbook.sheets && (
                                      <div className="bg-muted/20">
                                        {workbook.sheets.map((sheet) => (
                                          <div key={sheet.id} className="flex items-center gap-2 p-2 pl-12 text-sm text-muted-foreground">
                                            <FileText className="w-3 h-3" />
                                            <span>{sheet.name}</span>
                                          </div>
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>

                          {/* Files Section */}
                          <div>
                            <div 
                              className="flex items-center justify-between p-3 hover:bg-muted/50 cursor-pointer"
                              onClick={() => toggleFilesSection(space)}
                            >
                              <div className="flex items-center gap-2">
                                {space.isFilesLoading ? (
                                  <RefreshCw className="w-4 h-4 animate-spin text-muted-foreground" />
                                ) : (
                                  <FileText className="w-4 h-4 text-green-500" />
                                )}
                                <span className="text-sm">
                                  Files
                                  {getFilesForSpace(space.id).length > 0 && (
                                    <span className="ml-1 text-xs text-muted-foreground">
                                      ({getFilesForSpace(space.id).length})
                                    </span>
                                  )}
                                </span>
                                {/* Files Pagination */}
                                {explorerPagination.files.pageCount > 1 && space.filesExpanded && (
                                  <div className="flex items-center gap-1 ml-auto mr-2">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      disabled={explorerPagination.files.currentPage === 1}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        changeFilesPage(space.id, explorerPagination.files.currentPage - 1);
                                      }}
                                      className="p-1 h-auto"
                                    >
                                      <ChevronRight className="w-3 h-3 rotate-180" />
                                    </Button>
                                    <span className="text-xs text-muted-foreground">
                                      {explorerPagination.files.currentPage} / {explorerPagination.files.pageCount}
                                    </span>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      disabled={explorerPagination.files.currentPage === explorerPagination.files.pageCount}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        changeFilesPage(space.id, explorerPagination.files.currentPage + 1);
                                      }}
                                      className="p-1 h-auto"
                                    >
                                      <ChevronRight className="w-3 h-3" />
                                    </Button>
                                  </div>
                                )}
                              </div>
                              {space.filesExpanded ? (
                                <ChevronDown className="w-4 h-4 text-muted-foreground" />
                              ) : (
                                <ChevronRight className="w-4 h-4 text-muted-foreground" />
                              )}
                            </div>
                            
                            {space.filesExpanded && (
                              <div className="bg-background/50">
                                {getFilesForSpace(space.id).map((file) => (
                                  <div key={file.id} className="flex items-center justify-between p-3 pl-8 hover:bg-muted/50">
                                    <div className="flex items-center gap-2">
                                      <FileText className="w-4 h-4 text-green-500" />
                                      <span className="text-sm" title={file.name}>
                                        {truncateFileName(file.name)}
                                      </span>
                                    </div>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => selectNode(file, 'file')}
                                      className="text-xs p-1 h-auto"
                                    >
                                      <Search className="w-3 h-3" />
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex items-center justify-center h-32">
                  <div className="text-center text-muted-foreground">
                    <div className="text-sm">No spaces found</div>
                    <div className="text-xs mt-1">Try refreshing to load explorer data</div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right Pane - Details */}
          <div className="flex flex-col" style={{ width: '65%' }}>
            <div className="p-4 border-b border-border">
              <h3 className="text-base font-semibold">Details</h3>
            </div>
            
            <div className="flex-1 overflow-auto p-4">
              {selectedNode ? (
                <div className="bg-muted rounded-md p-4">
                  <pre className="text-xs text-foreground whitespace-pre-wrap break-words">
                    {JSON.stringify(selectedNode, null, 2)}
                  </pre>
                </div>
              ) : (
                <div className="flex items-center justify-center h-32">
                  <div className="text-center text-muted-foreground">
                    <div className="text-sm">Select a space, workbook, or file to view details</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
